/**
 * Whether a numeric histogram is binned on a linear or a logarithmic axis, and where its frame ends —
 * decided MECHANICALLY from the quantile ladder `probe_columns` already returns.
 *
 * Layer: shared/infra leaf. Pure arithmetic over numbers; no connection, no clock, no state.
 *
 * WHY THIS EXISTS. `WIDTH_BUCKET(col, low, high, 24)` gives equal-width bins on a linear axis. On a
 * column where p99/p50 is ~54, bucket 1 holds two thirds of the population BY CONSTRUCTION, and the
 * other 23 bars draw at the frontend's 4% minimum-height floor — dashes that are not data. No frontend
 * change can recover the shape, because bucket 1 is a single number: re-binning it would require
 * assuming the values inside it are uniform, which is exactly the assumption that is false.
 *
 * Equal-width buckets over `ln(value)` are GEOMETRIC buckets over the value — each bin a constant
 * MULTIPLE wider than the one before it rather than a constant amount. 24 bins from 127 to 383,703 step
 * ~1.4x at a time (127-177, 177-247, 247-345, ...) instead of 24 identical $16k slabs.
 *
 * WHY NOT LET THE AGENT DECIDE. Every input here is already measured, the rule is four comparisons, and
 * a model choosing per column would make the same column render differently on two loads. Mechanical is
 * both cheaper and reproducible. The agent never sees this decision.
 */
/** The binning space. `log1p` is `log` for a column that contains zero — `ln(0)` raises on Postgres. */
export type BinScale = 'linear' | 'log' | 'log1p';

export interface ScaleTransform {
  /** Value -> binning space. */
  to: (v: number) => number;
  /** Binning space -> value. Exact inverse of `to` for in-domain inputs. */
  from: (v: number) => number;
}

/**
 * The smallest positive value `log` will transform. A value at or below it is pinned here rather than
 * sent to `-Infinity`, which would poison an axis position or a bin edge with `NaN`.
 *
 * It is REACHABLE, and the earlier claim that it was not ("`chooseNumericBinning` never picks `log` for
 * a column whose `min` is not positive") did not follow: `min > 0` does not imply `min >= 1e-9`. A rate
 * or a probability column with `min: 1e-12` picks `log` legitimately. `chooseNumericBinning` therefore
 * CLAMPS the low edge to this value, so the frame it reports is the frame the SQL binds; without that
 * clamp, rows in `[min, LOG_FLOOR)` landed in `belowLow` while bin 1's reported start claimed them.
 * A column that far below the floor is 9+ decades of empty axis anyway — the clamp costs it nothing.
 */
const LOG_FLOOR = 1e-9;

export function scaleTransform(scale: BinScale): ScaleTransform {
  switch (scale) {
    case 'log':
      return { to: (v) => Math.log(Math.max(v, LOG_FLOOR)), from: (v) => Math.exp(v) };
    case 'log1p':
      return { to: (v) => Math.log1p(Math.max(v, 0)), from: (v) => Math.expm1(v) };
    case 'linear':
      return { to: (v) => v, from: (v) => v };
  }
}

/** The next finite IEEE-754 value above `value`, or `null` when no finite value exists. */
export function nextFloatUp(value: number): number | null {
  if (!Number.isFinite(value)) return null;
  if (Object.is(value, -0) || value === 0) return Number.MIN_VALUE;

  const buffer = new ArrayBuffer(8);
  const view = new DataView(buffer);
  view.setFloat64(0, value, false);
  const bits = view.getBigUint64(0, false);
  view.setBigUint64(0, value > 0 ? bits + 1n : bits - 1n, false);
  const next = view.getFloat64(0, false);
  return Number.isFinite(next) && next > value ? next : null;
}

/**
 * The exclusive upper edge of a half-open histogram frame `[low, high)`.
 *
 * Bins compare in BINNING space (value space under linear, `ln` / `log1p` under log). The target is
 * therefore the next representable number in that space. Walk value-space successors until the value
 * the SQL will actually bind reaches that target; mapping the target back once is insufficient because
 * `exp(nextFloatUp(log(x)))` can round straight back to `x` when `|log(x)|` is small.
 */
export function exclusiveFrameHigh(high: number, scale: BinScale): number | null {
  const transform = scaleTransform(scale);
  const transformedHigh = transform.to(high);
  const transformedTarget = nextFloatUp(transformedHigh);
  if (transformedTarget === null) return null;

  const mappedTarget = transform.from(transformedTarget);
  let candidate = Number.isFinite(mappedTarget) && mappedTarget > high ? mappedTarget : nextFloatUp(high);
  while (candidate !== null && transform.to(candidate) < transformedTarget) {
    candidate = nextFloatUp(candidate);
  }
  return candidate;
}

/**
 * What the decision reads. Every field is already produced by `probe_columns` — `min`/`max` are in
 * `DEFAULT_METRICS.numeric`, the percentiles are `PROFILE_QUANTILES` members behind
 * `include: ["quantiles"]`. Nothing here needs a new probe or a new quantile level.
 */
export interface NumericBinningStats {
  min: number;
  max: number;
  p01: number;
  p05: number;
  p25: number;
  p50: number;
  p75: number;
  p95: number;
  p99: number;
}

/**
 * How crowded the FIRST linear bin has to be before log binning is worth the surprise of a non-linear
 * axis, named as the LADDER LEVEL that bin's upper edge has to reach.
 *
 * `0.25` reads: "the first bar alone swallows the bottom quarter of the customers".
 *
 * WHY THIS AND NOT A QUANTILE RATIO. The defect is not a ratio; it is "bucket 1 holds 67% of the
 * audience and the other 23 bars are the minimum-height floor". A ratio like `p99 / p50 >= 50` is a
 * PROXY for that, and proxies drift from the thing they stand for: measured against real families,
 * a lognormal with sigma = 1.5 has `p99 / p50` of only 33 — under any sane fixed threshold — while its
 * first linear bin still holds 56% of the population. That chart is broken and the proxy said it was
 * fine. Measuring the crowding directly cannot disagree with the complaint, because it IS the complaint.
 *
 * It is also SCALE-FREE and BIN-COUNT-FREE by construction, which a fixed ratio is only accidentally.
 * Multiplying every value by a million changes nothing here; neither does moving off 24 bins. (For the
 * record, `p99 / p50` was scale-free too — a ratio always is — so "large numbers" was never the failure
 * mode. The failure mode was that a ratio is not the defect.)
 *
 * WHY A LADDER LEVEL AND NOT AN INTERPOLATED SHARE. The first draft of THIS rule interpolated a CDF
 * across the ladder to get bin 1's share as a percentage, then compared it to a multiple of `1/binCount`.
 * That works, but it assumes rows are spread uniformly BETWEEN two knots — and when bin 1's edge lands
 * in the middle of a wide segment, the percentage it produces is a guess inside that segment. Comparing
 * against a knot instead needs no such assumption: it is two measured numbers and a `<=`. The cost is
 * granularity — only the probed levels are reachable as thresholds — and for a binary decision that is
 * not a cost at all. It is arguably a feature: there are two sensible settings, `0.25` and `0.5`, and
 * both are sentences a human can check against a real chart.
 *
 * WHY NOT LOG-SPACE SKEWNESS, e.g. `(p50/p01) / (p99/p50)`. That measures asymmetry OF THE LOG LADDER,
 * and it is exactly 1.0 for a lognormal — which is the family log binning serves best, turning it into
 * a clean bell curve. It answers "would a log axis be ENOUGH?", a real question, but not this one.
 * This one is "is the LINEAR axis broken?", and that is a linear-space measurement.
 *
 * TUNING — there are exactly two sane settings, and only one alternative to the current one:
 *   `0.25` (today): the first bar holds a quarter of the population. Measured, framed p01..p99 over 24
 *          bins: uniform does not reach p05 (at ANY magnitude); exponential stops at 0.20 against a p25
 *          of 0.29; lognormal sigma=1 clears p25 by a hair; sigma=1.5 clears p50; the ACT2-569 column
 *          clears p50 with room to spare.
 *   `0.5`:  only fires once the first bar swallows the MEDIAN. Move here if charts that read fine come
 *          back looking stretched or logarithmic for no reason a reader can see — it drops the
 *          borderline lognormal-sigma=1 case and keeps everything worse than it.
 * Do NOT set it to `0.05`: a uniform column's first bin lands just past p05, so that would put every
 * column on a log axis.
 */
export const CROWDING_LEVEL = 0.25;

/**
 * How much of the TRACK the top 1% may occupy, when a log frame reaches out to the column's true `max`.
 *
 * A BUDGET, NOT A RATIO. `max / p99 <= 100` was the first draft and it is the wrong shape: 100 is a
 * different amount of chart on a column spanning two decades than on one spanning eight, so a fixed
 * ratio silently means different things on different columns. The budget form says what we actually
 * care about — "the outer 1% may take at most a quarter of the slider" — and is therefore relative to
 * the chart by construction.
 *
 * The reference span is `log(p99/p01)`, the core that is ALWAYS in frame (see the containment
 * invariant), so the budget cannot be gamed by the very edge it is deciding about.
 *
 * A log axis absorbs a long tail cheaply — a couple of extra decades cost a fixed slice, not the whole
 * chart — so on log we PREFER the real extremes and fall back to p99 only past this budget.
 *
 * TUNING. Lower it toward 0.1 if the right-hand end of the chart is reliably empty. Raise it toward 0.5
 * if users complain the handle cannot reach the real maximum, or that the top of the range is missing.
 */
export const LOG_TAIL_BUDGET = 0.25;

/**
 * The same budget for the LOW end — how much of the track the bottom 1% may occupy — and deliberately
 * far smaller than the tail's.
 *
 * The two ends are not symmetric, because what lies beyond them is not symmetric. Above p99 sit the
 * biggest customers: 1% of the population, but the end of the range a marketer actually drags towards,
 * and worth paying chart width to reach. Below p01 sits dust — a 0.0001 rounding artefact, a test row —
 * which nobody sets a threshold at. Width spent down there is width the visible population does not get.
 *
 * Worked example, the ACT2-569 column: the core `log(p99/p01)` is `ln(383703/127)` = 8.01, so the head
 * budget is 0.80. Reaching `min` = 0.5 would cost `ln(127/0.5)` = 5.54 — nearly 40% of the slider bought
 * for the bottom 1% — so the frame stays at p01. Reaching `max` costs `ln(398526/383703)` = 0.04 against
 * a tail budget of 2.0, so the frame takes the true max. Two different answers from one rule, both for
 * the right reason.
 *
 * TUNING. Raise it if reviewers report a frame that visibly fails to reach a real, populated minimum.
 * Lower it toward 0 if charts come back with dead space on the left.
 */
export const LOG_HEAD_BUDGET = 0.1;

/**
 * On LINEAR, how close `min` (or `max`) must be to `p01` (`p99`) before the frame widens out to it,
 * as a fraction of the p01..p99 span. p01/p99 stays the default and the fallback: on a linear axis one
 * absurd outlier is exactly what empties the chart.
 *
 * TUNING. Raise it if reviewers report a frame that should obviously have reached the real min/max and
 * did not. Lower it if a single outlier is observed dragging a frame out.
 */
export const LINEAR_WIDEN_FRACTION = 0.05;

/**
 * Whether every number a warehouse actually returned for this column is a whole one.
 *
 * Judged on the LADDER only — the nine measured values — never on computed bin edges, which come out
 * fractional on any column. Same evidence and same heuristic the frontend's `isIntegerValued` already
 * uses. Nine integral quantiles on a genuinely fractional column is close enough to impossible to
 * ignore, and the failure direction is safe: mistaking a float column for an integer one only DECLINES
 * a log axis, which is today's behaviour.
 */
function isIntegerLadder(stats: NumericBinningStats): boolean {
  const { min, max, p01, p05, p25, p50, p75, p95, p99 } = stats;
  return [min, max, p01, p05, p25, p50, p75, p95, p99].every(Number.isInteger);
}

/**
 * The COMB GUARD: geometric bins are narrowest at the low end, and a bin narrower than the column's own
 * granularity aliases.
 *
 * On an integer column an `order_count` framed 0..48 under log1p gives a first bin 0.176 wide, and the
 * histogram comes back holding `1 0 0 0 1 0 1 0 1 1 0 1 1 1 2 2 ...` distinct integers per bin —
 * measured, not hypothetical. A comb is a WORSE lie than a flat linear chart, because it looks like
 * structure.
 *
 * WHY NOT A DISTINCT-COUNT PROXY. The first draft was `approxDistinct >= 3 * binCount`. That column has
 * **126** distinct values against a guard of 72, so the proxy waved it straight through — the same
 * mistake the `p99 / p50` trigger made, and caught the same way: guard the MECHANISM (a bin narrower
 * than one integer) rather than something correlated with it. `approxDistinct` is not read by this
 * module at all any more, and has been dropped from the stats it asks for.
 *
 * KNOWN LIMITATION, deliberate. Under `log1p` anchored at zero the first bin is always a fraction wide,
 * so an integer column reaching zero can never take a log axis at 24 bins — `page_views` framed 0..5000
 * comes out at 0.426. That is structural, not a threshold to tune: 24 geometric bins cannot resolve
 * small integers. The two real fixes are a dynamic bin count and a dedicated zero bucket with log bins
 * above it, and BOTH are out of scope here (see Global Constraints). Staying linear is the honest
 * answer in the meantime, and it is exactly today's behaviour.
 */
function combsUnderLog(
  stats: NumericBinningStats,
  frame: { scale: BinScale; low: number; high: number },
  binCount: number,
): boolean {
  if (!isIntegerLadder(stats)) return false;
  const t = scaleTransform(frame.scale);
  const width = (t.to(frame.high) - t.to(frame.low)) / binCount;
  // The FIRST bin is the narrowest under both log scales, so it is the only one that has to be checked.
  return t.from(t.to(frame.low) + width) - frame.low < 1;
}

/**
 * How much room the rule's own threshold gets between itself and the edge of the chart, as a fraction
 * of the frame — measured in the BINNING space, so it is the same two bins wide on either scale.
 *
 * WHY THE THRESHOLD IS AN INPUT AT ALL. The frame is chosen from the column's shape, but the control is
 * read with a rule already on it. A rule of `ltv > 130` against a frame starting at p01 = 127 puts the
 * handle on the left edge: the reader can drag it tighter and cannot drag it looser, and the chart
 * offers no evidence about the population it would pick up if they did. Extending the frame past the
 * threshold restores both.
 *
 * WHY NOT JUST JUMP TO `min`/`max`. That is the obvious version of this rule and it is too blunt on a
 * log axis: a threshold one step above p01 = 127 on a column whose `min` is 0.0001 would drag four
 * empty decades — 40% of the track — into view to buy one bin of drag room. Extending by a MARGIN
 * bounded by the extremes gets the room and keeps the chart. When `min` happens to be close, the clamp
 * lands on it anyway, which is the same answer by a route that also handles the far case.
 *
 * TWO INVARIANTS, both load-bearing:
 *  - The anchor may only WIDEN. This is what keeps a column-keyed cache honest: `audience_sketches`
 *    stores ONE histogram per column, so a frame chosen for one rule's threshold is reused by a sibling
 *    rule on the same column. Widen-only means that frame is always a superset of the default one, so
 *    the sibling is never worse off than it is today. Narrowing would make one rule's chart depend on
 *    another rule's value, which is not a property anyone could debug.
 *  - The frame never leaves `[min, max]`. A threshold beyond the data (`ltv > 10000000`) must show an
 *    empty selection against the real distribution, not a mostly-blank chart stretched to reach it.
 *
 * TUNING. `2/24` is two bins of headroom. Raise it if the handle still reads as pinned to the edge.
 * Lower it toward 0 if anchored charts come back visibly wider than unanchored ones for no benefit.
 */
export const ANCHOR_MARGIN_FRACTION = 2 / 24;

const finite = (v: number): boolean => typeof v === 'number' && Number.isFinite(v);

/**
 * The ladder LEVEL that the first of `binCount` LINEAR bins reaches, over the p01..p99 frame.
 *
 * Returns the `q` of the highest knot at or below bin 1's upper edge — so `0.5` means "the first bar
 * alone contains everyone up to the median". That is the crowding measurement, stated in the only
 * currency this data actually has: measured quantiles.
 *
 * NO INTERPOLATION, on purpose. An earlier draft estimated bin 1's share as a percentage by
 * interpolating a CDF between knots, which assumes rows are spread uniformly inside a segment — and
 * when bin 1's edge falls in the middle of a wide segment, that percentage is a guess about the inside
 * of that segment. This is two measured numbers and a comparison, and it cannot be wrong about them.
 *
 * WHY THE p01..p99 FRAME AND NOT THE CHOSEN ONE. This decides WHETHER to go log, and the frame rules run
 * AFTER the scale is known, so the final frame does not exist yet. p01..p99 is also the frame today's
 * broken charts are drawn on, which makes it the honest thing to ask the question about. And it is the
 * conservative choice: every widening the frame rules or the anchor can apply makes bin 1 WIDER and so
 * more crowded, never less — so testing here can only under-detect, never fire spuriously.
 */
export function firstBinReaches(stats: NumericBinningStats, binCount: number): number {
  const span = stats.p99 - stats.p01;
  // A collapsed core is a point mass, not a distribution: the first bin holds everything by definition.
  // Rule 2's cardinality guard is what actually stops such a column reaching a log axis; this just does
  // not have to pretend otherwise.
  if (!(span > 0) || !(binCount > 0)) return 1;
  const edge = stats.p01 + span / binCount;
  const knots: [number, number][] = [
    [stats.p01, 0.01],
    [stats.p05, 0.05],
    [stats.p25, 0.25],
    [stats.p50, 0.5],
    [stats.p75, 0.75],
    [stats.p95, 0.95],
    [stats.p99, 0.99],
  ];
  // The LAST knot at or below the edge wins, which is also what makes a plateau read correctly: a column
  // that is half zeros has min..p50 all at 0, and the run's TOP level is the honest answer for that value.
  let reached = 0;
  for (const [value, q] of knots) {
    if (value <= edge) reached = q;
  }
  return reached;
}

/**
 * The scale and frame for one numeric column. Total and deterministic: every refusal falls back to
 * `linear` with today's p01/p99 frame, so no input can produce something the old code path could not.
 *
 * INVARIANT: THE RETURNED FRAME ALWAYS CONTAINS `[p01, p99]`. Every candidate edge is `min` or `p01` on
 * the left and `max` or `p99` on the right, and the anchor may only widen — so the frame grows outward
 * and never inward. The service can move a selected `max` to its next representable value because the
 * final edge is exclusive. At least 98% of the non-null population is inside the resulting frame, and
 * `belowLow`/`aboveHigh` report the rest.
 *
 * This is deliberate, and it is the same rule `PROFILE_QUANTILES` was chosen under: cutting at p05/p95
 * would discard 10% of the population, which is visible and wrong on a control that claims to be the
 * distribution, where p01/p99 discards 2%. It is NOT an oversight that no rule here can decide to frame
 * a long p95..p99 stretch away — and the case for doing so is weakest exactly where it would be most
 * tempting, because a log axis is what compresses that stretch. A caller that genuinely needs a tighter
 * frame still has the explicit `low`/`high` override.
 *
 * The invariant is emergent from five separate rules, so it is pinned by a sweep test rather than left
 * to this comment. Do not remove that test when tuning a constant.
 */
export function chooseNumericBinning(
  stats: NumericBinningStats,
  binCount: number,
  /** The threshold(s) the rule already holds — one value, or two for a range. See
   *  `ANCHOR_MARGIN_FRACTION`. Absent, or non-finite, ⇒ the frame is the column's alone. */
  anchor?: readonly number[],
): { scale: BinScale; low: number; high: number } {
  const { min, max, p01, p50, p75, p99 } = stats;

  // The linear answer is also the SAFE answer, so it is built first and returned from every decline
  // below. p01/p99 is the frame today; the widening is opt-in on top of it, never the other way round.
  const linear = (): { scale: BinScale; low: number; high: number } => {
    const span = p99 - p01;
    const slack = span > 0 ? span * LINEAR_WIDEN_FRACTION : 0;
    // The anchor applies HERE too, not only on the log branch — `withAnchor`'s own docblock says so and
    // for a while only the log return went through it, which silently dropped the threshold on every
    // linear column (the majority of them). A rule sitting on the edge of the chart is the same defect
    // on either axis, and the widening is scale-agnostic by construction: `scaleTransform('linear')` is
    // the identity, so "two bins of headroom in the binning space" is two bins on either one. It is
    // still widen-only and still clamped to `[min, max]`, so an anchored linear frame remains a superset
    // of the unanchored one and the cache invariant is untouched.
    return withAnchor(
      {
        scale: 'linear',
        low: finite(min) && p01 - min <= slack ? min : p01,
        high: finite(max) && max - p99 <= slack ? max : p99,
      },
      stats,
      anchor,
    );
  };

  // An unusable ladder is not an argument for log — it is an argument for changing nothing. A single
  // NaN or a non-monotonic ladder (which `rows.ts` already refuses to publish) lands here.
  const { p05, p25, p95 } = stats;
  if (![min, max, p01, p05, p25, p50, p75, p95, p99].every(finite)) return linear();
  if (!(min <= p01 && p01 <= p05 && p05 <= p25 && p25 <= p50 && p50 <= p75 && p75 <= p95 && p95 <= p99 && p99 <= max)) {
    return linear();
  }
  if (!(p01 < p99)) return linear();

  // 1. Negatives. `ln` is undefined and there is no rescue here: a signed-log or an offset shift makes
  //    the axis unexplainable ("what is -3 on this chart?") for a control a marketer reads at a glance.
  if (min < 0) return linear();

  // 2. Crowding. The one trigger. Everything else is a veto; this is the reason — and it is the
  //    customer-visible defect measured directly, not a proxy for it. See CROWDING_LEVEL.
  if (firstBinReaches(stats, binCount) < CROWDING_LEVEL) return linear();

  // `log1p` for a column that reaches zero: `ln(0)` RAISES on Postgres and kills the whole statement,
  // turning a supported column into one that looks unsupported. `log1p(0)` is 0, so zero stays a legal,
  // and preferred, low edge.
  const scale: BinScale = min <= 0 ? 'log1p' : 'log';

  // Both ends are decided against a BUDGET measured in track width, not against a fixed ratio — see
  // LOG_TAIL_BUDGET. The core `log(p99/p01)` is the reference because it is always in frame.
  // ASYMMETRIC on purpose: the tail above p99 is cheap and wanted; the head below p01 is expensive and
  // usually junk.
  const t = scaleTransform(scale);
  const core = t.to(p99) - t.to(p01);
  const headCost = p01 > 0 && min > 0 ? t.to(p01) - t.to(min) : 0;
  const tailCost = t.to(max) - t.to(p99);
  const chosenLow = min <= 0 || p01 <= 0 || headCost <= core * LOG_HEAD_BUDGET ? min : p01;
  const high = tailCost <= core * LOG_TAIL_BUDGET ? max : p99;
  // `LOG_FLOOR` used to be documented as unreachable from here on the grounds that `log` needs `min > 0`.
  // That does not follow: `min > 0` does not imply `min >= 1e-9`. A rate or a probability column with
  // `min: 1e-12` picks `log`, reports `low: 1e-12`, and binds `LN(1e-9)` — so every row in
  // `[1e-12, 1e-9)` is counted in `belowLow` while bin 1's REPORTED start claims to cover it. Clamping
  // here is what makes the reported frame and the bound frame the same number; the alternative (leave
  // the frame and correct the docblock) keeps a histogram whose first bin is a lie about its own edge.
  const low = scale === 'log' ? Math.max(chosenLow, LOG_FLOOR) : chosenLow;

  // A frame that collapsed cannot be binned at all. Refusing to `linear` here rather than throwing keeps
  // this function total — the callers' own edge validation still runs downstream.
  if (!(low < high)) return linear();

  // 3. The comb guard runs LAST, because it is the only rule that needs the finished frame: whether the
  //    narrowest bin clears one integer depends on where the frame's ends landed, and those are not
  //    known until the budgets above have run. See `combsUnderLog`.
  const framed = { scale, low, high };
  if (combsUnderLog(stats, framed, binCount)) return linear();

  // ...and it runs AGAIN on the ANCHORED frame, because the anchor can widen the LOW edge and that makes
  // bin 1 narrower in value terms: the width is `low^((n-1)/n) * high^(1/n) - low`, which RISES with
  // `low` and rises with `high`, so pulling `low` down shrinks it. A marginal integer column that
  // cleared the guard at `framed` can be pushed back under one-unit bins by the extra headroom.
  //
  // WHAT GIVES WAY IS THE ANCHOR, NOT THE SCALE. Returning `linear()` here — the first spelling of this
  // check — traded one defect for a worse one: `linear()` is not the pre-anchor log frame but a
  // different answer entirely (it re-derives both edges from p01/p99 and `LINEAR_WIDEN_FRACTION`), so
  // passing a threshold could flip the scale AND pull `high` inward from `max` to `p99`. That breaks
  // three published promises — the tool description, the contract doc, and ACT2-569's own acceptance
  // list all say the anchor changes neither the scale nor the frame's outward direction — and it breaks
  // them in the way that matters most: `audience_sketches` stores ONE histogram per column, so whichever
  // sibling rule probed last would decide the scale for all of them.
  //
  // The anchor is a nicety; the scale is the substance. So the low-edge widening is dropped and the
  // high-edge one kept, which cannot comb: bin-1 width rises with `high`, so a frame widened only
  // upward is always at least as wide in bin 1 as `framed`, which already passed.
  const anchored = withAnchor(framed, stats, anchor);
  if (!combsUnderLog(stats, anchored, binCount)) return anchored;
  return { scale, low: framed.low, high: anchored.high };
}

/**
 * Widen a frame so the rule's own threshold is not sitting on the edge of it.
 *
 * Applied to the LINEAR answer too, and by the same arithmetic: `scaleTransform('linear')` is the
 * identity, so "two bins of headroom in the binning space" is two bins of headroom on either axis, with
 * no branch. That is the whole reason the margin is expressed as a fraction of the transformed span
 * rather than as a value delta.
 */
function withAnchor(
  frame: { scale: BinScale; low: number; high: number },
  stats: NumericBinningStats,
  anchor: readonly number[] | undefined,
): { scale: BinScale; low: number; high: number } {
  const values = (anchor ?? []).filter(finite);
  if (values.length === 0) return frame;

  const t = scaleTransform(frame.scale);
  const lowT = t.to(frame.low);
  const highT = t.to(frame.high);
  const margin = (highT - lowT) * ANCHOR_MARGIN_FRACTION;
  if (!finite(margin) || margin <= 0) return frame;

  // Clamp to the column's own extremes FIRST, so a threshold outside the data cannot stretch the chart
  // to reach it — an empty selection against a real distribution is the honest picture there.
  const wantLowT = t.to(Math.max(stats.min, Math.min(...values))) - margin;
  const wantHighT = t.to(Math.min(stats.max, Math.max(...values))) + margin;

  // WIDEN-ONLY, never narrow. See the invariants on ANCHOR_MARGIN_FRACTION — this is what keeps a
  // column-keyed histogram cache honest.
  //
  // An untouched edge is returned AS IT WAS, never round-tripped through the transform. The obvious
  // spelling — `t.from(Math.min(lowT, wantLowT))` — perturbs an edge it is not even widening, because
  // `Math.exp(Math.log(127))` is 126.99999999999999. That ULP is not cosmetic: `low` is bound as a
  // SQL parameter and is reported back as the frame, so an anchored call and a bare one on the same
  // column would disagree in the last digit for no reason anyone could debug.
  const low = wantLowT < lowT ? Math.max(stats.min, t.from(wantLowT)) : frame.low;
  const high = wantHighT > highT ? Math.min(stats.max, t.from(wantHighT)) : frame.high;
  if (!(low < high) || !finite(low) || !finite(high)) return frame;
  return { scale: frame.scale, low, high };
}
